import React from 'react'
import { useRouteMatch, useLocation } from 'react-router-dom'

import { ArtboardDetailInfoFragment } from '@sketch/gql-types'
import { RouteParams, routes, IS_EMBEDDED } from '@sketch/constants'
import { CWV_ROUTES } from 'utils/route'
import {
  useForLargerThanMobile,
  IsEmbedded,
  NotEmbedded,
} from '@sketch/components'
import { formatProjectName } from 'modules/projects/utils'
import { ShareWithoutVersion } from 'modules/versioning'
import useUserSignedIn from 'modules/user/useUserSignedIn'

import { NavigationContainer } from './HeaderNavigation.styles'
import NavigationItem from './NavigationItem'
import ArtboardsNavigationItem from './ArtboardsNavigationItem'
import PagesNavigationItem from './PagesNavigationItem'
import CWVGroupNavigationItem from './CWVGroupNavigationItem'
import Breadcrumbs from './Breadcrumbs'
import { useIsCanvasFallback } from '../../hooks/useIsCanvasFallback'
import { useShareViewsNavigation } from 'modules/shares/utils/shareViewsNavigation'

// get label and link for PROJECT item
const useProjectNav = (share: ShareWithoutVersion) => {
  const isMobile = !useForLargerThanMobile()
  const isSignedIn = useUserSignedIn()
  const project = share.project
  const projectName = project ? formatProjectName(project) : ''

  if (!projectName || !isSignedIn || IS_EMBEDDED || isMobile) {
    return null
  }

  const projectId = project?.identifier ?? ''
  const workspaceId = share.workspace.identifier
  const projectLink =
    project?.type === 'PERSONAL_DRAFTS'
      ? routes.WORKSPACE_DRAFTS.create({ workspaceId })
      : routes.WORKSPACE_PROJECT.create({ projectId, workspaceId })

  return { projectName, projectLink }
}

// check PAGE item visibility
const usePageNav = () => {
  const match = useRouteMatch<RouteParams<'SHARE_PAGE_VIEW'>>()
  const isCwvRoute = !!CWV_ROUTES.find((route: string) => route === match.path)

  // hide on CWV route or if number of pages < 2
  return !isCwvRoute
}

export type HeaderNavigationProps = {
  share: ShareWithoutVersion
  artboard?: ArtboardDetailInfoFragment
  className?: string
}

const HeaderNavigation: React.FC<HeaderNavigationProps> = ({
  share,
  artboard,
}) => {
  const projectNav = useProjectNav(share)
  const showPage = usePageNav()
  const isCanvasFallback = useIsCanvasFallback()
  const { previousView } = useShareViewsNavigation()
  const location: {
    state?: { groupId: string; symbolName: string }
  } = useLocation()

  const documentName = share.name
  const documentLink = isCanvasFallback
    ? routes.SHARE_VIEW_GRID.create({ shareID: share.identifier })
    : routes.SHARE_VIEW.create({ shareID: share.identifier })
  const collapseDocument = showPage && !!artboard

  // make sure we're coming from a symbol and its data is set
  const isSymbolsView =
    previousView?.view === 'symbols' &&
    location.state?.groupId &&
    location.state?.symbolName

  return (
    <NavigationContainer>
      <NotEmbedded>
        <Breadcrumbs>
          {projectNav && (
            <NavigationItem
              collapsed={true}
              to={projectNav.projectLink}
              icon="project"
              label={projectNav.projectName}
            />
          )}
          <NavigationItem
            collapsed={collapseDocument}
            to={documentLink}
            icon={share.type === 'TEMPLATE' ? 'template' : 'document'}
            label={documentName}
          />
          {isSymbolsView ? (
            <CWVGroupNavigationItem
              shareIdentifier={share.identifier}
              groupId={location.state?.groupId ?? ''}
              symbolName={location.state?.symbolName ?? ''}
            />
          ) : (
            <PagesNavigationItem artboard={artboard} share={share} />
          )}
          {!isSymbolsView && artboard && (
            <ArtboardsNavigationItem
              artboard={artboard}
              shareIdentifier={share.identifier}
            />
          )}
        </Breadcrumbs>
      </NotEmbedded>
      <IsEmbedded>
        <Breadcrumbs>
          {!artboard && (
            <NavigationItem
              to={documentLink}
              icon="document"
              label={documentName}
            />
          )}
          {!artboard && (
            <PagesNavigationItem artboard={artboard} share={share} />
          )}
          {artboard && (
            <ArtboardsNavigationItem
              artboard={artboard}
              shareIdentifier={share.identifier}
            />
          )}
        </Breadcrumbs>
      </IsEmbedded>
    </NavigationContainer>
  )
}

export default HeaderNavigation
